import React from "react";
import { NavigationProp, ParamListBase } from "@react-navigation/native";
import { StyleSheet, TouchableOpacity, View, ViewProps } from "react-native";
import { useSelector } from "react-redux";
import { Chat } from "src/components";
import BrandTermsLink from "src/components/Chat/BrandTermsLink";
import { kColors } from "src/kColors";
import { kStyleConst } from "src/kStyle";
import AcknowledgementBox from "src/components/Chat/AcknowledgementBox";
import { useUserProfile } from "src/hooks/ui";
import { PdText } from "src/components/layout";
import { kRoute } from "src/kRoute";
import WriteAnOrderButton from "src/components/Chat/WriteAnOrderButton";
import { queryBrandRepConnection } from "src/db/connection.db";

const PdDivider: React.FC<ViewProps & { orientation?: "horizontal" | "vertical" }> = ({
  orientation = "vertical",
  style,
  ...rest
}) => (
  <View style={[orientation === "vertical" ? styles.pdDividerVertical : styles.pdDividerHorizontal, style]} {...rest} />
);

const P04_01_Conversation: React.FC<{ navigation: NavigationProp<ParamListBase> }> = ({ navigation }) => {
  const { isAdmin, isBrand, isRetailer, isRep } = useUserProfile();
  const rCompanyId = useSelector((state) => state.UserReducer.profile.companyRefPath.split("/")?.[1]);
  const rCompany = useSelector(({ CompanyReducer }) => CompanyReducer);
  const currentThread = useSelector(({ MessageThreadsReducer: { rThreads, rCurrentThreadId } }) =>
    rThreads.find((myMessageThread) => myMessageThread.id === rCurrentThreadId),
  );
  const threadDetails = React.useMemo(() => {
    const parts = currentThread?.recipient.split("/") ?? ["", ""];
    const accountType = parts[0];
    const accountId = parts[1];
    return { accountType, accountId };
  }, [currentThread?.recipient]);

  const handleOnPressTitle = async () => {
    if (threadDetails.accountType === "boutiques") {
      if (isAdmin) navigation.navigate(kRoute.P06_10, { retailerId: threadDetails.accountId });
      else navigation.navigate(kRoute.P10, { id: threadDetails.accountId, connectionId: currentThread?.id });
    }

    if (threadDetails.accountType === "brands") {
      if (isAdmin) navigation.navigate(kRoute.P06_09, { brandId: threadDetails.accountId });
      else navigation.navigate(kRoute.P100, { brand: currentThread?.recipientStr });
    }

    if (threadDetails.accountType === "reps") {
      if (isAdmin) navigation.navigate(kRoute.P06_11, { repId: threadDetails.accountId });
      else navigation.navigate(kRoute.P20, { id: threadDetails.accountId });
    }
  };

  const handleOnPressWriteAnOrder = async () => {
    if (isBrand && rCompany.type === "brand") {
      navigation.navigate(kRoute.P100, { brand: rCompany.name });
    } else if (isRep) {
      const connectionsQuery = await queryBrandRepConnection({ repId: rCompanyId }).get();
      if (connectionsQuery.size > 1) {
        navigation.navigate(kRoute.P99);
      } else if (connectionsQuery.docs[0]) {
        navigation.navigate(kRoute.P100, { brand: connectionsQuery.docs[0].data().business.name });
      }
    }
  };

  return (
    <View style={styles.flex1}>
      <View style={styles.headerChatContainer}>
        <View style={styles.headerChatLeftContainer}>
          {currentThread && (
            <TouchableOpacity onPress={handleOnPressTitle}>
              <PdText numberOfLines={1} variant="body-lg" style={styles.headerChatTitle}>
                {currentThread.recipientStr}
              </PdText>
            </TouchableOpacity>
          )}
          {isRetailer && currentThread?.recipient.startsWith("brands") && (
            <BrandTermsLink brandId={currentThread?.recipient.split("/")?.[1]} />
          )}
        </View>
        {(isBrand || isRep) && <WriteAnOrderButton onPress={handleOnPressWriteAnOrder} />}
      </View>
      <PdDivider />
      {(isBrand || isRetailer) && <AcknowledgementBox />}
      <Chat thread={currentThread} />
    </View>
  );
};

export const P04_01 = React.memo(P04_01_Conversation);

const styles = StyleSheet.create({
  flex1: {
    flex: 1,
  },
  headerChatContainer: {
    alignItems: "flex-end",
    flexDirection: "row",
    justifyContent: "space-between",
    paddingHorizontal: kStyleConst.kSpacing * 4,
    paddingVertical: kStyleConst.kSpacing * 3,
  },
  headerChatLeftContainer: {
    flex: 1,
    marginRight: kStyleConst.kSpacing * 2,
  },
  headerChatTitle: {
    textDecorationLine: "underline",
  },
  pdDividerHorizontal: {
    backgroundColor: kColors.gray300,
    height: "100%",
    width: StyleSheet.hairlineWidth,
  },
  pdDividerVertical: {
    backgroundColor: kColors.gray300,
    height: StyleSheet.hairlineWidth,
  },
});
